import { storage } from "@/utils";
import { config } from "@/config";
import DOMPurify from "dompurify";
import { triggerReAuth, isReAuthModalOpen } from "@/stores/auth/reauth-store";
import { saveBeforeNavigate } from "@/utils/safeNavigation";
import { useAuthStore } from "@/stores/auth/auth-store";

interface SecurityPolicy {
  maxSessionDuration: number;
  requireSecureTransport: boolean;
  enableCSP: boolean;
  enableXSSProtection: boolean;
  allowedDomains: string[];
  forbiddenPatterns: RegExp[];
}

interface SecurityEvent {
  type: string;
  severity: "low" | "medium" | "high" | "critical";
  timestamp: number;
  details: unknown;
  userAgent?: string;
  url?: string;
}

interface CSPViolation {
  blockedURI: string;
  disposition: string;
  documentURI: string;
  effectiveDirective: string;
  originalPolicy: string;
  violatedDirective: string;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
}

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keypress",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "focus",
  "input",
  "pointerdown",
] as const;

const isInputElement = (
  target: EventTarget | null,
): target is HTMLInputElement =>
  target instanceof HTMLInputElement;

class FrontendSecurityService {
  private securityPolicy: SecurityPolicy;
  private securityEvents: SecurityEvent[] = [];
  private sessionStartTime: number;
  private lastActivityTime: number;
  private isSecurityEnabled: boolean = true;

  // Track interval IDs for cleanup
  private sessionCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private inactivityCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private storageCheckIntervalId: ReturnType<typeof setInterval> | null = null;

  // Track event listener references for cleanup
  private activityListener: (() => void) | null = null;
  private inputListener: ((e: Event) => void) | null = null;
  private cspListener: ((e: SecurityPolicyViolationEvent) => void) | null = null;

  constructor() {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();

    this.securityPolicy = {
      maxSessionDuration: 8 * 60 * 60 * 1000, // 8 hours
      requireSecureTransport: true,
      enableCSP: true,
      enableXSSProtection: true,
      allowedDomains: [
        config.api.baseUrl,
        "https://api.terramatrix.com",
        "https://auth.terramatrix.com",
      ],
      forbiddenPatterns: [
        /<script[^>]*>.*?<\/script>/gi,
        /javascript:/gi,
        /vbscript:/gi,
        /onload=/gi,
        /onerror=/gi,
        /onclick=/gi,
      ],
    };

    this.initializeSecurity();
  }

  /**
   * Initialize security measures
   */
  private initializeSecurity(): void {
    this.setupCSPViolationReporting();
    this.setupSessionMonitoring();
    this.setupInputValidation();
    this.setupXSSProtection();
    this.setupSecurityHeaders();
    this.monitorLocalStorage();
    this.setupIntegrityChecking();
  }

  /**
   * Setup Content Security Policy violation reporting
   */
  private setupCSPViolationReporting(): void {
    if (!this.securityPolicy.enableCSP) return;

    this.cspListener = (e: SecurityPolicyViolationEvent) => {
      const violation: CSPViolation = {
        blockedURI: e.blockedURI,
        disposition: e.disposition,
        documentURI: e.documentURI,
        effectiveDirective: e.effectiveDirective,
        originalPolicy: e.originalPolicy,
        violatedDirective: e.violatedDirective,
        sourceFile: e.sourceFile,
        lineNumber: e.lineNumber,
        columnNumber: e.columnNumber,
      };

      this.logSecurityEvent({
        type: "csp_violation",
        severity: "high",
        timestamp: Date.now(),
        details: violation,
        url: this.getSafeCurrentUrl(),
      });

      // Report to backend
      this.reportSecurityViolation("csp_violation", violation);
    };

    document.addEventListener(
      "securitypolicyviolation",
      this.cspListener,
    );
  }

  /**
   * Setup session monitoring
   */
  private setupSessionMonitoring(): void {
    // Monitor session duration
    this.sessionCheckIntervalId = setInterval(() => {
      const sessionDuration = Date.now() - this.sessionStartTime;
      if (sessionDuration > this.securityPolicy.maxSessionDuration) {
        this.handleSessionTimeout();
      }
    }, 60000); // Check every minute

    // Monitor user activity
    this.activityListener = () => {
      this.lastActivityTime = Date.now();
    };

    const activityListener = this.activityListener;
    if (activityListener) {
      ACTIVITY_EVENTS.forEach((event) => {
        document.addEventListener(event, activityListener, { passive: true });
      });
    }

    // Check for inactivity
    this.inactivityCheckIntervalId = setInterval(() => {
      const inactivityDuration = Date.now() - this.lastActivityTime;
      if (inactivityDuration > 30 * 60 * 1000) {
        // 30 minutes
        this.handleInactivity();
      }
    }, 60000);
  }

  /**
   * Validate input for malicious content
   */
  public validateInput(input: string, type: string = "text"): boolean {
    // Check for XSS patterns
    for (const pattern of this.securityPolicy.forbiddenPatterns) {
      if (pattern.test(input)) {
        this.logSecurityEvent({
          type: "malicious_input",
          severity: "high",
          timestamp: Date.now(),
          details: {
            input: input.substring(0, 200),
            type: type,
            pattern: pattern.toString(),
          },
          url: this.getSafeCurrentUrl(),
        });
        return false;
      }
    }

    return true;
  }

  /**
   * Sanitize HTML content using DOMPurify
   */
  public sanitizeHTML(html: string): string {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "i",
        "b",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "blockquote",
        "div",
        "span",
        "img",
        "a",
      ],
      ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "id"],
      FORBID_TAGS: [
        "script",
        "object",
        "embed",
        "form",
        "input",
        "button",
        "style",
        "link",
      ],
      FORBID_ATTR: [
        "onclick",
        "onload",
        "onerror",
        "onmouseover",
        "onfocus",
        "onblur",
        "onchange",
        "onsubmit",
        "onkeydown",
        "onkeyup",
        "onkeypress",
      ],
      KEEP_CONTENT: true,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      SANITIZE_DOM: true,
    });
  }

  /**
   * Log security event
   */
  private logSecurityEvent(event: SecurityEvent): void {
    event.userAgent = navigator.userAgent;
    this.securityEvents.push(event);

    // Keep only last 100 events
    if (this.securityEvents.length > 100) {
      this.securityEvents = this.securityEvents.slice(-100);
    }

    // Log to console in development
    if (config.app.debug) {
      console.warn("Security Event:", {
        type: event.type,
        severity: event.severity,
        timestamp: event.timestamp,
        url: event.url,
        detailKeys:
          event.details && typeof event.details === "object"
            ? Object.keys(event.details)
            : undefined,
      });
    }
  }

  /**
   * Report security violation to backend
   */
  private async reportSecurityViolation(
    type: string,
    details: unknown,
  ): Promise<void> {
    try {
      await fetch(`${config.api.baseUrl}/security/violations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type,
          details,
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
          url: this.getSafeCurrentUrl(),
          referrer: this.getSafeReferrer(),
        }),
      });
    } catch (error) {
      console.error("Failed to report security violation:", error);
    }
  }

  private getSafeCurrentUrl(): string {
    return `${window.location.origin}${window.location.pathname}`;
  }

  private getSafeReferrer(): string {
    if (!document.referrer) return "";

    try {
      const referrerUrl = new URL(document.referrer);
      return `${referrerUrl.origin}${referrerUrl.pathname}`;
    } catch {
      return "";
    }
  }

  /**
   * Handle session timeout — triggers re-auth modal instead of hard redirect.
   * Saves editor work first if applicable.
   */
  private handleSessionTimeout(): void {
    // Signed-out users can sit on public pages indefinitely; there is no
    // authenticated session to recover.
    if (!useAuthStore.getState().isAuthenticated) return;

    this.logSecurityEvent({
      type: "session_timeout",
      severity: "medium",
      timestamp: Date.now(),
      details: {
        sessionDuration: Date.now() - this.sessionStartTime,
      },
      url: this.getSafeCurrentUrl(),
    });

    // Don't open a second modal if one is already showing
    if (isReAuthModalOpen()) return;

    // Save editor work before triggering re-auth
    saveBeforeNavigate().finally(() => {
      triggerReAuth("session_expired");
    });
  }

  /**
   * Handle user inactivity — dispatches a custom event for a non-blocking
   * React modal instead of using blocking `confirm()`.
   */
  private handleInactivity(): void {
    // Don't warn unauthenticated users — there's no session to expire
    if (!useAuthStore.getState().isAuthenticated) return;

    // Re-auth is the terminal session state; do not stack an inactivity warning.
    if (isReAuthModalOpen()) return;

    this.logSecurityEvent({
      type: "user_inactivity",
      severity: "low",
      timestamp: Date.now(),
      details: {
        inactivityDuration: Date.now() - this.lastActivityTime,
      },
      url: this.getSafeCurrentUrl(),
    });

    // Dispatch event for the InactivityWarningModal (React layer) to handle
    window.dispatchEvent(
      new CustomEvent("terramatrix:inactivity-warning"),
    );
  }

  /**
   * Reset activity timer — called by InactivityWarningModal when user clicks "Continue"
   */
  public resetActivity(): void {
    this.resetSessionClocks("inactivity_continue");
  }

  /**
   * Reset authenticated-session timers after an explicit re-authentication.
   */
  public resetAuthenticatedSession(reason: "reauth_success"): void {
    this.resetSessionClocks(reason);
  }

  private resetSessionClocks(reason: string): void {
    const now = Date.now();
    const previousSessionDuration = now - this.sessionStartTime;
    const previousInactivityDuration = now - this.lastActivityTime;

    this.sessionStartTime = now;
    this.lastActivityTime = now;

    this.logSecurityEvent({
      type: "session_timer_reset",
      severity: "low",
      timestamp: now,
      details: {
        reason,
        previousSessionDuration,
        previousInactivityDuration,
      },
      url: this.getSafeCurrentUrl(),
    });
  }

  /**
   * Clear sensitive data from storage
   */
  private clearSensitiveData(): void {
    // Clear tokens and user data
    storage.remove(config.storage.keys.tokens);
    storage.remove(config.storage.keys.user);

    // Clear any other sensitive data
    const sensitiveKeys = [
      "access_token",
      "refresh_token",
      "user_session",
      "auth_state",
    ];

    sensitiveKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  /**
   * Setup input validation
   */
  private setupInputValidation(): void {
    this.inputListener = (e: Event) => {
      const target = e.target;
      if (isInputElement(target) && target.value) {
        this.validateInput(target.value, target.type || "text");
      }
    };

    document.addEventListener("input", this.inputListener);
  }

  /**
   * Setup XSS protection
   */
  private setupXSSProtection(): void {
    if (!this.securityPolicy.enableXSSProtection) return;
    // Basic XSS protection is already implemented in validateInput and sanitizeHTML
  }

  /**
   * Setup security headers validation
   */
  private setupSecurityHeaders(): void {
    // Validate that we're running over HTTPS in production
    if (
      this.securityPolicy.requireSecureTransport &&
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      this.logSecurityEvent({
        type: "insecure_transport",
        severity: "critical",
        timestamp: Date.now(),
        details: {
          protocol: window.location.protocol,
          hostname: window.location.hostname,
        },
        url: this.getSafeCurrentUrl(),
      });
    }
  }

  /**
   * Monitor localStorage for security issues
   */
  private monitorLocalStorage(): void {
    // Check for sensitive data in localStorage
    const sensitivePatterns = [
      /password/i,
      /credit.?card/i,
      /ssn/i,
      /social.?security/i,
      /api.?key/i,
      /secret/i,
    ];

    this.storageCheckIntervalId = setInterval(
      () => {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            const value = localStorage.getItem(key) || "";

            for (const pattern of sensitivePatterns) {
              if (pattern.test(key) || pattern.test(value)) {
                this.logSecurityEvent({
                  type: "sensitive_data_in_storage",
                  severity: "medium",
                  timestamp: Date.now(),
                  details: {
                    key: key,
                    pattern: pattern.toString(),
                  },
                  url: this.getSafeCurrentUrl(),
                });
              }
            }
          }
        }
      },
      5 * 60 * 1000,
    ); // Check every 5 minutes
  }

  /**
   * Setup integrity checking for critical resources
   */
  private setupIntegrityChecking(): void {
    // Basic integrity checking implementation
  }

  /**
   * Get security metrics for dashboard
   */
  public getSecurityMetrics() {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;

    const recentEvents = this.securityEvents.filter(
      (event) => now - event.timestamp < oneHour,
    );

    const eventsByType: Record<string, number> = {};
    const eventsBySeverity: Record<string, number> = {};

    recentEvents.forEach((event) => {
      eventsByType[event.type] = (eventsByType[event.type] || 0) + 1;
      eventsBySeverity[event.severity] =
        (eventsBySeverity[event.severity] || 0) + 1;
    });

    return {
      totalEvents: recentEvents.length,
      eventsByType,
      eventsBySeverity,
      sessionDuration: now - this.sessionStartTime,
      lastActivity: now - this.lastActivityTime,
      securityEnabled: this.isSecurityEnabled,
    };
  }

  /**
   * Enable/disable security monitoring
   */
  public toggleSecurity(enabled: boolean): void {
    this.isSecurityEnabled = enabled;

    this.logSecurityEvent({
      type: "security_toggle",
      severity: "medium",
      timestamp: Date.now(),
      details: {
        enabled,
      },
      url: this.getSafeCurrentUrl(),
    });
  }

  /**
   * Get recent security events
   */
  public getRecentEvents(count: number = 10): SecurityEvent[] {
    return this.securityEvents.slice(-count);
  }

  /**
   * Force security check
   */
  public performSecurityCheck(): void {
    this.monitorLocalStorage();

    // Check current URL
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      this.logSecurityEvent({
        type: "insecure_connection",
        severity: "high",
        timestamp: Date.now(),
        details: {
          protocol: window.location.protocol,
        },
        url: this.getSafeCurrentUrl(),
      });
    }
  }

  /**
   * Destroy all intervals and event listeners for HMR cleanup.
   */
  public destroy(): void {
    if (this.sessionCheckIntervalId) {
      clearInterval(this.sessionCheckIntervalId);
      this.sessionCheckIntervalId = null;
    }
    if (this.inactivityCheckIntervalId) {
      clearInterval(this.inactivityCheckIntervalId);
      this.inactivityCheckIntervalId = null;
    }
    if (this.storageCheckIntervalId) {
      clearInterval(this.storageCheckIntervalId);
      this.storageCheckIntervalId = null;
    }

    // Remove activity listeners
    if (this.activityListener) {
      const activityListener = this.activityListener;
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, activityListener);
      });
      this.activityListener = null;
    }

    // Remove input listener
    if (this.inputListener) {
      document.removeEventListener("input", this.inputListener);
      this.inputListener = null;
    }

    // Remove CSP listener
    if (this.cspListener) {
      document.removeEventListener(
        "securitypolicyviolation",
        this.cspListener,
      );
      this.cspListener = null;
    }
  }
}

export const securityService = new FrontendSecurityService();

// HMR cleanup: prevent duplicate timers/listeners on hot reload
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    securityService.destroy();
  });
}
